import { Skeleton } from '@mui/material';
import { useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AdminSettingsFormData } from 'src/components/pages/Settings/AdminSettings/AdminSettings';
import { getDynamicSettings } from 'src/features/globalSlice';
import { WhiteLabelConfig } from 'src/store/models/WhiteLabel';
import { useLoadingSelector } from 'src/utils/useLoadingSelector';

import Input from '../Input/Input';
import classes from './WhiteLabelsConfig.module.scss';

interface WhiteLabelsConfigProps {
  originalValues: WhiteLabelConfig[];
}

const WhiteLabelsConfig = ({ originalValues }: WhiteLabelsConfigProps) => {
  const { t } = useTranslation();

  const [currentLabel, setCurrentLabel] = useState(-1);

  const loading = useLoadingSelector({ actionType: getDynamicSettings.type });

  const { control, watch, setValue, formState, getValues, reset } =
    useFormContext<AdminSettingsFormData>();
  const { fields } = useFieldArray({
    control,
    name: 'whiteLabelsConfig',
  });
  const whiteLabel = watch(`whiteLabelsConfig.${currentLabel}`);

  const changedLabels: WhiteLabelConfig[] =
    getValues()?.whiteLabelsConfig?.flatMap((l, i) => {
      return formState?.dirtyFields?.whiteLabelsConfig?.[i] ? l : [];
    });

  const resetChanges = () => {
    reset({ ...getValues(), whiteLabelsConfig: originalValues });
  };

  const change = (label: WhiteLabelConfig, key: number) => {
    const original = originalValues?.find((o) => o?.appName === label?.appName);
    return (
      <div key={key}>
        <span>
          {`${t('settings:white_label')}:`}
          <b>{label?.appName}</b>
        </span>
        {original?.value?.maxOpenTickets !== label?.value?.maxOpenTickets && (
          <span>
            {`${t('settings:max_active_tickets')}:`}
            <b>{`${original?.value?.maxOpenTickets} ⮕ ${label?.value?.maxOpenTickets}`}</b>
          </span>
        )}
        {original?.value?.showManufacturers !==
          label?.value?.showManufacturers && (
          <span>
            {`${t('settings:show_manufacturers')}:`}
            <b>{`${
              original?.value?.showManufacturers
                ? t('common:yes')
                : t('common:no')
            } ⮕ ${
              label?.value?.showManufacturers ? t('common:yes') : t('common:no')
            }`}</b>
          </span>
        )}
      </div>
    );
  };

  return loading ? (
    <div className={classes.Wrapper}>
      <Skeleton animation="wave" variant="text" />
      <Skeleton animation="wave" variant="text" />
      <Skeleton animation="wave" variant="text" />
    </div>
  ) : (
    <div className={classes.Wrapper}>
      <Input
        label={t('settings:white_label')}
        select
        value={currentLabel}
        onChange={(e) => setCurrentLabel(Number(e?.target?.value))}
      >
        <option disabled value={-1}>{`${t('common:choose')}...`}</option>
        {fields?.map((v, i) => (
          <option key={i} value={i}>
            {v.appName}
          </option>
        ))}
      </Input>
      <Input
        label={t('settings:max_active_tickets')}
        select
        value={whiteLabel?.value?.maxOpenTickets || -1}
        onChange={(e) =>
          setValue(
            `whiteLabelsConfig.${currentLabel}`,
            {
              ...whiteLabel,
              value: {
                ...whiteLabel.value,
                maxOpenTickets: Number(e?.target?.value),
              },
            },
            { shouldDirty: true }
          )
        }
      >
        <option disabled value={-1}>{`${t('common:choose')}...`}</option>
        {whiteLabel &&
          [...Array(10)]?.map((_, i) => (
            <option key={i} value={i + 1}>
              {i + 1}
            </option>
          ))}
      </Input>
      <Input
        label={t('settings:show_manufacturers')}
        select
        value={
          whiteLabel
            ? whiteLabel?.value?.showManufacturers
              ? t('common:yes')
              : t('common:no')
            : -1
        }
        onChange={(e) =>
          setValue(
            `whiteLabelsConfig.${currentLabel}`,
            {
              ...whiteLabel,
              value: {
                ...whiteLabel.value,
                showManufacturers: e?.target?.value === t('common:yes'),
              },
            },
            { shouldDirty: true }
          )
        }
      >
        <option disabled value={-1}>{`${t('common:choose')}...`}</option>
        {whiteLabel &&
          [t('common:yes'), t('common:no')]?.map((v, i) => (
            <option key={i} value={v}>
              {v}
            </option>
          ))}
      </Input>
      {formState?.dirtyFields?.whiteLabelsConfig ? (
        <div className={classes.ChangeLog}>
          <p>{t('settings:change_log')}</p>
          <section>{changedLabels?.map((l, i) => change(l, i))}</section>
          <button type="button" onClick={resetChanges}>
            {t('settings:reset_changes')}
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default WhiteLabelsConfig;
